import type { SelectOption } from '@scaleaq/scaleui';
import type { CameraInfo, WebRtcStream } from '@scaleaq/sitecontrol-lib';

import { PresetModal } from '@/components/cameraStreamWidget/cameraStreamSideControls/PresetModal.tsx';
import { responsive } from '@/utils';

import { CameraStreamProfileToggleButton } from './cameraStreamSideControls/CameraStreamProfileToggleButton.tsx';
import { StyledSideControlsRail } from './cameraStreamSideControls/cameraStreamSideControls.styles.ts';
import { CameraSwitchPanel } from './cameraStreamSideControls/CameraSwitchPanel.tsx';
import type { CameraSwitching } from './cameraSwitchPanel/cameraSwitchPanel.types.ts';

type CameraStreamSideControlsProps = {
  camera: CameraInfo;
  cameraSwitching?: CameraSwitching;
  selectedStream: SelectOption<WebRtcStream> | undefined;
  streamOptions: SelectOption<WebRtcStream>[] | undefined;
  profileSwitching: boolean;
  profileToggleDisabled: boolean;
  /** Unmount the quality (HQ/LQ) toggle entirely — used during DVR rewind, which always
      recalls the HIGH tier and so has no tier for the user to choose. */
  profileToggleHidden?: boolean;
  onProfileToggle: (stream: SelectOption<WebRtcStream>) => void;
  zIndex: number;
};

/** Right-side rail: camera group switcher and stream-quality toggle. */
export const CameraStreamSideControls = ({
  camera,
  cameraSwitching,
  selectedStream,
  streamOptions,
  profileSwitching,
  profileToggleDisabled,
  profileToggleHidden = false,
  onProfileToggle,
  zIndex,
}: CameraStreamSideControlsProps) => {
  const cameraId = camera.id;
  const profileStreams = streamOptions && streamOptions.length > 1 ? streamOptions : undefined;
  const showProfileToggle = Boolean(selectedStream && profileStreams) && !profileToggleHidden;
  // Nothing to show: don't render the rail at all (keeps it out of the hover/hit area).
  return (
    <StyledSideControlsRail
      direction='column'
      alignment='bottom-right'
      gap={responsive.spacing['3xlarge']}
      fullWidth={false}
      $zIndex={zIndex}
    >
      {cameraSwitching && (
        <CameraSwitchPanel
          cameras={cameraSwitching.siblings}
          selectedCameraId={cameraId}
          onSelectCamera={cameraSwitching.onSelect}
          onNextCamera={cameraSwitching.onCycle}
        />
      )}
      {showProfileToggle && selectedStream && profileStreams && (
        <CameraStreamProfileToggleButton
          currentStream={selectedStream}
          streams={profileStreams}
          loading={profileSwitching}
          disabled={profileToggleDisabled}
          onToggle={onProfileToggle}
        />
      )}
      <PresetModal camera={camera} />
    </StyledSideControlsRail>
  );
};
